import { User } from '@auth0/auth0-react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';

import { Auth0AppUser, CustomPermission, AppUser, QueryOption, LoggedInUser } from '../@types';
import {
  createClientAuthSide,
  getAuthUserById,
  sendWelcomeEmail,
  updateAuthUserById,
} from '../api/auth';
import { uploadUserProfileImage } from '../api/uploadedFiles';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  updateUserEmailVerification,
  getUserPermissions,
  createUserPermissions,
  createUserWhenLogin,
  getUserByAuth0Id,
  getUserSystemPermissions,
  getLoggedInUserData,
  updateProfileConfig,
} from '../api/users';
import type { UserListResponse } from '../api/users';
import { USER_OBJECT_KEY } from '../configs/envs';
import {
  AppUserPermissionKey,
  AppUserSystemPermissionKey,
  AppUsersGetOneKey,
  AppUsersKey,
  AuthAppUserKey,
} from '../configs/users';
import { ProfileConfig } from '../pages/Profile/Profile.props';

export type { UserListResponse } from '../api/users';

// User creation data
interface UserCreateData extends Partial<AppUser> {
  profilePicDetails?: FileList;
}

// User update data - separate from AppUser because profilePic can be a FileList during update
interface UserUpdateData {
  id: number;
  profilePic?: FileList | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  contactEmail?: string;
  auth0Id?: string;
  isActive?: boolean;
  isLocked?: boolean;
  isEmailVerified?: boolean;
  timezoneId?: number;
  notes?: string;
  systemRoleId?: number;
  structureId?: number;
}

// Auth0 update payload
interface Auth0UpdatePayload {
  id: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  blocked?: boolean;
  account_enabled?: boolean;
}

// Auth user from login provider
interface AuthUserFromProvider {
  sub?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  email?: string;
  blocked?: boolean;
  picture?: string;
  updated_at?: string;
}

export const useGetAllUsers = (queryOption: QueryOption): UseQueryResult<UserListResponse> => {
  return useQuery({
    queryKey: [AppUsersKey, queryOption.page, queryOption.pageSize, queryOption.filterModel],
    queryFn: () => getUsers(queryOption),
  });
};

export const useGetOneUser = (id: number): UseQueryResult<AppUser> => {
  return useQuery({
    queryKey: [AppUsersGetOneKey, id],
    queryFn: () => getUserById(id),
    select: (response: AppUser) => response,
  });
};

export const useGetCurrentUser = (): UseQueryResult<AppUser> => {
  const userObj = localStorage.getItem(USER_OBJECT_KEY);
  const user = userObj ? (JSON.parse(userObj) as { sub: string }) : null;

  return useQuery({
    queryKey: user ? [AppUsersKey, user.sub] : ['disabled'],
    queryFn: async () => {
      if (!user) throw new Error('User object not found');
      return getUserByAuth0Id(user.sub);
    },
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCreateUser = (): UseMutationResult<{ id: number }, Error, UserCreateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserCreateData) => {
      const { email, firstName, lastName, profilePicDetails } = data;
      if (!email || !firstName || !lastName) {
        throw new Error('Email, first name, and last name are required');
      }
      const authResponse = await createClientAuthSide(email, firstName, lastName, '');
      const userData: Partial<AppUser> = {
        ...data,
        contactEmail: email,
        auth0Id: authResponse.user_id,
        lastActive: authResponse.updated_at,
        isActive: true,
        isLocked: false,
        isEmailVerified: false,
        structureId: 1,
      };

      const response = await createUser(userData);

      if (response?.id && profilePicDetails?.[0]) {
        const formData = new FormData();
        formData.append('fileList', profilePicDetails[0]);
        await uploadUserProfileImage(response.id, formData);
      }

      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppUsersKey],
      });
    },
  });
};

export const useUpdateUser = (): UseMutationResult<void, Error, UserUpdateData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UserUpdateData) => {
      const { profilePic, id, ...restData } = data;
      const updatedData: Partial<AppUser> = { ...restData };

      if (profilePic && typeof profilePic !== 'string') {
        const formData = new FormData();
        formData.append('fileList', profilePic[0]);
        const uploadedFileResponses = await uploadUserProfileImage(id, formData);
        updatedData.profilePic = uploadedFileResponses[0].url;
      } else if (typeof profilePic === 'string') {
        updatedData.profilePic = profilePic;
      }

      await updateUser(id, updatedData);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppUsersKey],
      });
    },
  });
};

export const useGetUserPermissions = (id: string): UseQueryResult<CustomPermission[]> => {
  return useQuery({
    queryKey: [AppUserPermissionKey, id],
    queryFn: () => getUserPermissions(id),
  });
};

export const useGetUserSystemPermissions = (): UseQueryResult<CustomPermission[]> => {
  return useQuery({
    queryKey: [AppUserSystemPermissionKey],
    queryFn: () => getUserSystemPermissions(),
    staleTime: 60 * 60 * 1000,
  });
};

export const useAddUserPermissions = (): UseMutationResult<
  void,
  Error,
  { id: string; customPermissions: CustomPermission[] }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; customPermissions: CustomPermission[] }) =>
      createUserPermissions(data.id, data.customPermissions),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppUsersKey],
      });
    },
  });
};

export const useGetOneAuthUser = (id: string): UseQueryResult<Auth0AppUser | undefined> => {
  return useQuery({
    queryKey: [AuthAppUserKey, id],
    queryFn: async () => {
      if (!id) return undefined;
      return getAuthUserById(id);
    },
  });
};

export const useSendWelcomeEmail = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => await sendWelcomeEmail(id),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey],
      });
    },
  });
};

export const useUpdateAuth0User = (): UseMutationResult<void, Error, Auth0UpdatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Auth0UpdatePayload) => {
      const { id, email, name, given_name, family_name, blocked, account_enabled } = data;
      await updateAuthUserById(id, {
        email,
        name,
        given_name,
        family_name,
        blocked,
        account_enabled,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey],
      });
    },
  });
};

export const useAuthUser = (
  user: User | AuthUserFromProvider | null | undefined
): UseMutationResult<void, Error, AuthUserFromProvider> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (authUser: AuthUserFromProvider) => {
      if (!authUser?.sub) return;

      const authUserResponse = await getUserByAuth0Id(authUser.sub).catch((error: Error) => {
        console.error('Error fetching user by Auth0 Id:', error);
        return undefined;
      });

      if (authUserResponse) {
        // trigger this when user's email verified from auth0 and not verified in DB
        if (authUser?.email_verified && !authUserResponse.isEmailVerified) {
          await updateUserEmailVerification(authUserResponse.id, { isEmailVerified: true });
        }
      } else {
        await createUserWhenLogin(authUser);
      }
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey, user?.sub],
      });
    },
  });
};

export const useGetLoggedInUserData = (): UseQueryResult<LoggedInUser> => {
  return useQuery({
    queryKey: [AppUsersKey],
    queryFn: () => getLoggedInUserData(),
  });
};

export const useUpdateProfileConfig = (
  userId: number
): UseMutationResult<void, Error, { config: ProfileConfig }> => {
  return useMutation({
    mutationFn: async (data: { config: ProfileConfig }) => {
      await updateProfileConfig(userId, data.config);
    },
  });
};
